import { NetworkStatus, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import LoadingIndicator from 'primitives/LoadingIndicator';
import TextField from 'primitives/TextField';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';

import debounce from 'utils/debounce';

import AddUserToSyndicateDialogButton from './AddUserToSyndicateDialogButton';
import SyndicateUserDialog from './SyndicateUserDialog';

const USERS_QUERY = gql(`
  query fetchUsersBySyndicate($syndicateId: ID!, $cursor: ID, $limit: Int, $filters: SyndicateUsersFilterType) {
    syndicate(id: $syndicateId) {
      id
      syndicateUsers(cursor: $cursor, limit: $limit, filters: $filters) {
        nodes {
          id
          user {
            id
            name
            email
          }
          createdAt
        }
        pageInfo {
          hasNextPage
          totalCount
          cursor
        }
      }
    }
  }
`);

const SyndicateUsers: FC = () => {
  const [syndicateUserOnDisplay, setSyndicateUserOnDisplay] = useState('');
  const { syndicateId } = useParams<{ syndicateId: string }>() as { syndicateId: string };

  const { loading, error, data, refetch, fetchMore, variables, networkStatus } = useQuery(
    USERS_QUERY,
    {
      variables: {
        limit: 50,
        syndicateId,
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  if (loading && !data) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const syndicate = data.syndicate;
  const syndicateUsers = syndicate.syndicateUsers.nodes;
  const pageInfo = syndicate.syndicateUsers.pageInfo;

  return (
    <>
      <AddUserToSyndicateDialogButton syndicateId={syndicate.id} />
      {syndicateUserOnDisplay && (
        <SyndicateUserDialog
          syndicateUserId={syndicateUserOnDisplay}
          onClose={() => setSyndicateUserOnDisplay('')}
        />
      )}
      <div className="flex justify-between items-end">
        <TextField
          placeholder="Search for a user"
          onChange={debounce(e => {
            refetch({
              filters: {
                ...variables?.filters,
                text: e.target.value,
              },
            });
          }, 500)}
        />
        <span className="text-sm text-gray-500">
          {syndicateUsers.length} of {pageInfo.totalCount} members
        </span>
      </div>
      <DataTable
        data={syndicateUsers}
        onClick={user => setSyndicateUserOnDisplay(user.id)}
        columns={[
          {
            label: 'Name',
            fieldName: 'user.name',
          },
          {
            label: 'Email',
            fieldName: 'user.email',
          },
          {
            label: 'Joined At',
            fieldName: 'createdAt',
            type: 'DATETIME',
          },
        ]}
        hasNextPage={pageInfo.hasNextPage}
        paginationLoading={loading}
        filterLoading={networkStatus === NetworkStatus.setVariables}
        onLoadMore={() =>
          fetchMore({
            variables: {
              cursor: pageInfo.cursor,
            },
          })
        }
      />
    </>
  );
};

export default SyndicateUsers;
